/**
 * Stronghold (vanilla StrongholdPieces), much reduced: a stone-brick maze
 * deep underground of a crossing room, corridors, a library of bookshelves and
 * the portal room — a lava pool under a raised dais ringed by the twelve end
 * portal frames (each holds an eye of ender with a 10% chance, seeded).
 *
 * Placement is vanilla's concentric rings rather than random spread: 3, 6 and
 * 10 strongholds on rings whose radii are 1280..2816 blocks for the first and
 * 3072 further out for each next (the full 3/6/10/15/21/28/36/9 vanilla rings
 * are capped at three: ~19 strongholds, far beyond any plausible exploration).
 * Horizontal world scale is vanilla's, so no scale-down is applied. `site()`
 * feeds that to the structure generator; `nearestStronghold` serves the eye of
 * ender and is pure (no terrain needed). The portal room holds the silverfish
 * spawner (game/trials.ts runs it).
 */

import { AIR, END_PORTAL_FRAME_EYE_ID, END_PORTAL_FRAME_ID, LAVA_ID, SILVERFISH_SPAWNER_ID } from '../../blocks';
import { CHUNK_SIZE } from '../../constants';
import { hash2, mulberry32 } from '../../noise';
import { vy } from '../ctx';
import type { TerrainGenerator } from '../terrain';
import { block, type Builder, cell, F, centredPiece, type Piece, type Rng, seedOf, StructureType, toWorld } from './kit';

const BRICKS = cell.mix([
  [block('Stone Bricks'), 6], [block('Mossy Stone Bricks'), 2], [block('Cracked Stone Bricks'), 2],
]);
/** A frame that holds an eye with probability 0.1 (the builder's seeded stream). */
const FRAME: (b: Builder) => number = (b) => (b.rnd() < 0.1 ? END_PORTAL_FRAME_EYE_ID : END_PORTAL_FRAME_ID);
const TORCH = block('Torch');

const SALT = 0x5f0d;
const SPACING = 64; // chunks per placement region (site() maps rings onto it)
const RING_COUNTS = [3, 6, 10];
/** Piece size and the portal ring's centre in the piece's own frame (see buildStronghold). */
const W = 29, D = 37, CENTRE_U = 5, CENTRE_V = 29;

interface Site { cx: number; cz: number; rot: number }
const siteCache = new Map<number, readonly Site[]>();

/** Every stronghold of a world, deterministic in the seed. */
function strongholdSites(seed: number): readonly Site[] {
  let sites = siteCache.get(seed);
  if (sites) return sites;
  const out: Site[] = [];
  RING_COUNTS.forEach((count, ring) => {
    const rnd = mulberry32(Math.floor(hash2(ring, 7, seed ^ SALT) * 4294967296));
    const a0 = rnd() * Math.PI * 2;
    const base = 1280 + ring * 3072 + rnd() * 1536;
    for (let i = 0; i < count; i++) {
      const a = a0 + (Math.PI * 2 * (i + rnd() * 0.3)) / count;
      const r = base + (rnd() - 0.5) * 192;
      const cx = Math.floor((Math.cos(a) * r) / CHUNK_SIZE), cz = Math.floor((Math.sin(a) * r) / CHUNK_SIZE);
      out.push({ cx, cz, rot: Math.floor(hash2(cx, cz, seed ^ SALT) * 4) });
    }
  });
  if (siteCache.size > 8) siteCache.clear();
  siteCache.set(seed, out);
  return out;
}

/** The middle of a stronghold's portal ring (world x, z) for the site's start chunk. */
function portalCentre(s: Site): { x: number; z: number } {
  const [ox, oz] = toWorld(s.cx * CHUNK_SIZE + 8, s.cz * CHUNK_SIZE + 8, s.rot, -(W >> 1), -(D >> 1));
  const [x, z] = toWorld(ox, oz, s.rot, CENTRE_U, CENTRE_V);
  return { x, z };
}

/** The stronghold portal ring closest to world (x, z): the centre cell of its 3 x 3 opening. */
export function nearestStronghold(seed: number, x: number, z: number): { x: number; z: number } {
  let best = { x: 0, z: 0 };
  let bestD = Infinity;
  for (const s of strongholdSites(seed)) {
    const c = portalCentre(s);
    const d = (c.x - x) * (c.x - x) + (c.z - z) * (c.z - z);
    if (d < bestD) { bestD = d; best = c; }
  }
  return best;
}

/** Layout along v: crossing room (0..10), corridor (11..20), portal room (21..36);
 *  the library opens off the crossing to the side (u 12..26). */
function buildStronghold(b: Builder): void {
  const y = b.y;
  // crossing room with a pillar in the middle
  b.room(0, y, 0, 10, y + 6, 10, BRICKS);
  b.fill(5, y + 1, 5, 5, y + 5, 5, BRICKS);
  b.set(5, y + 3, 4, TORCH);
  // corridor to the portal room
  b.room(3, y, 10, 7, y + 4, 21, BRICKS);
  b.fill(4, y + 1, 10, 6, y + 3, 21, AIR);
  // the portal room
  b.room(-2, y, 21, 12, y + 8, 36, BRICKS);
  b.fill(1, y, 24, 9, y, 33, LAVA_ID);                 // the lava pool
  b.fill(0, y - 1, 23, 10, y - 1, 34, BRICKS);
  b.fill(3, y, 25, 7, y + 1, 33, BRICKS);             // the dais over it
  b.fill(4, y + 2, 28, 6, y + 2, 30, AIR);
  for (let k = 0; k < 3; k++) b.fill(4, y + 1 + k, 22 + k, 6, y + 1 + k, 22 + k, block('Stone Brick Stairs')); // the stair up (rises toward +z)
  b.fill(4, y + 1, 22, 6, y + 3, 21, AIR);
  // lava under the 3 x 3 opening, as in vanilla
  b.fill(4, y, 28, 6, y + 1, 30, LAVA_ID);
  // the twelve frame blocks round it
  for (let i = 0; i < 3; i++) {
    b.set(4 + i, y + 2, 27, FRAME); b.set(4 + i, y + 2, 31, FRAME);
    b.set(3, y + 2, 28 + i, FRAME); b.set(7, y + 2, 28 + i, FRAME);
  }
  for (const [u, v] of [[0, 23], [10, 23], [0, 34], [10, 34]]) b.set(u, y + 4, v, TORCH);
  b.set(5, y + 2, 33, SILVERFISH_SPAWNER_ID);
  // the library off the crossing room
  b.fill(10, y + 1, 4, 12, y + 3, 6, AIR);
  b.room(12, y, 0, 26, y + 7, 12, BRICKS);
  for (let v = 1; v <= 11; v++) for (const u of [13, 25]) b.fill(u, y + 1, v, u, y + 5, v, block('Bookshelf'));
  for (const u of [16, 19, 22]) for (let v = 2; v <= 10; v++) if (v !== 6) b.fill(u, y + 1, v, u, y + 3, v, block('Bookshelf'));
  b.fill(12, y + 1, 4, 12, y + 3, 6, AIR);
  b.chest(24, y + 1, 1, F.PV);
  b.set(19, y + 4, 6, TORCH);
}

function stronghold(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const cx = Math.floor(x / CHUNK_SIZE), cz = Math.floor(z / CHUNK_SIZE);
  const c = g.columnClimate(x, z);
  const y = Math.max(vy(10), Math.min(vy(25) + Math.floor(hash2(cx, cz, g.seed ^ SALT ^ 1) * 8), c.height - 24));
  const rot = Math.floor(hash2(cx, cz, g.seed ^ SALT) * 4);
  return [centredPiece(x, z, rot, W, D, y, false, seedOf(rnd), buildStronghold)];
}

export const STRONGHOLD: StructureType = {
  name: 'Stronghold', spacing: SPACING, separation: 24, salt: SALT, reach: 3, hud: false, generate: stronghold,
  site(seed, rx, rz) {
    for (const s of strongholdSites(seed)) {
      if (Math.floor(s.cx / SPACING) === rx && Math.floor(s.cz / SPACING) === rz) return { cx: s.cx, cz: s.cz };
    }
    return null;
  },
};
